import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@ideoz/ui';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-all disabled:pointer-events-none [&_svg]:pointer-events-none  shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
  {
    variants: {
      variant: {
        // Primary variants
        primary:
          'bg-primary-600 text-primary-25 rounded-lg border-2 border-primary-600 hover:bg-primary-700 focus-visible:ring-primary-600 disabled:bg-[#f5f5f5] disabled:border-[#e9eaeb] disabled:text-[#a4a7ae] dark:disabled:bg-gray-800 dark:disabled:border-gray-700 dark:disabled:text-gray-500',

        // Figma extracted variant - Primary with gradient border
        'primary-custom':
          'bg-primary-700 text-primary-25 rounded border-2 border-gradient-to-b from-white/12 to-white/0 hover:bg-[#2d3461] focus-visible:ring-primary-600 disabled:bg-[#f5f5f5] disabled:border-[#e9eaeb] disabled:text-[#a4a7ae]',

        // Secondary color variants
        'secondary-color':
          'bg-primary-50 text-primary-700 border border-primary-50 rounded-lg hover:bg-primary-200 hover:text-primary-700 focus-visible:ring-[#4b579f] disabled:bg-white disabled:border-[#e9eaeb] disabled:text-[#a4a7ae]',

        // Secondary gray variants
        'secondary-gray':
          'bg-white text-gray-600 border border-[#d5d7da] rounded-lg hover:bg-[#fafafa] hover:text-[#252b37] focus-visible:ring-[#d5d7da] disabled:bg-white disabled:border-[#e9eaeb] disabled:text-[#a4a7ae] dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-700 dark:hover:text-white dark:disabled:bg-gray-800 dark:disabled:border-gray-700 dark:disabled:text-gray-500',

        // Tertiary color variants
        'tertiary-color':
          'bg-transparent text-[#1d213a] rounded-lg hover:bg-[#d4d7eb] hover:text-[#1a1d2f] focus-visible:bg-white focus-visible:ring-[#4b579f] disabled:text-[#a4a7ae]',

        // Tertiary gray variants
        'tertiary-gray':
          'bg-transparent text-[#535862] rounded-lg hover:bg-[#fafafa] hover:text-[#414651] focus-visible:bg-white focus-visible:ring-[#d5d7da] disabled:text-[#a4a7ae]',

        // Link variants
        'link-color':
          'bg-transparent text-[#1d213a] rounded-lg hover:text-[#1a1d2f] focus-visible:bg-white/10 focus-visible:rounded disabled:text-[#a4a7ae]',

        'link-gray':
          'bg-transparent text-[#535862] rounded-lg hover:text-[#414651] focus-visible:bg-white/10 focus-visible:rounded disabled:text-[#a4a7ae]',

        // Legacy variants for backward compatibility
        default:
          'bg-primary-700 text-primary-25 rounded-lg border-2 border-primary-700 hover:bg-primary-800 focus-visible:ring-primary-600 disabled:bg-[#f5f5f5] disabled:border-[#e9eaeb] disabled:text-[#a4a7ae]',
        outline:
          'rounded-lg border border-border bg-surface px-3.5 py-1.5 text-sm font-semibold shadow-soft transition-colors hover:border-brand/40 hover:text-brand',
        destructive:
          'bg-error-600 text-white border-2 border-error-600 rounded-lg hover:bg-error-700 focus-visible:ring-error-200 disabled:bg-error-200 disabled:border-error-200 disabled:text-white',
        secondary:
          'bg-white text-gray-600 border border-[#d5d7da] rounded-lg hover:bg-[#fafafa] hover:text-[#252b37] focus-visible:ring-[#d5d7da] disabled:bg-white disabled:border-[#e9eaeb] disabled:text-[#a4a7ae] dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-700 dark:hover:text-white dark:disabled:bg-gray-800 dark:disabled:border-gray-700 dark:disabled:text-gray-500',
        ghost:
          'bg-transparent text-[#535862] rounded-lg hover:bg-[#fafafa] hover:text-[#414651] focus-visible:bg-white focus-visible:ring-[#d5d7da] disabled:text-[#a4a7ae]',
        link: 'bg-transparent text-[#1d213a] rounded-lg hover:text-[#1a1d2f] focus-visible:bg-white/10 focus-visible:rounded disabled:text-[#a4a7ae]',
      },
      size: {
        xs: 'h-[30px] px-3 py-2 text-xs gap-1 has-[>svg]:px-2 !font-semibold', // 30px height
        sm: 'h-9 px-4 py-3 text-sm gap-1.5 has-[>svg]:px-2.5', // 36px height
        default: 'h-8 px-3 text-sm gap-2 has-[>svg]:px-3', // 40px height
        lg: 'h-11 px-6 text-base gap-2 has-[>svg]:px-4', // 44px height
        xl: 'h-12 px-6 text-base gap-2 has-[>svg]:px-4', // 48px height
        '2xl': 'h-15 px-8 text-lg gap-3 has-[>svg]:px-6', // 60px height

        // Icon only variants
        'icon-sm': 'size-9 p-0', // 36x36px
        'icon-default': 'size-10 p-0', // 40x40px
        'icon-lg': 'size-11 p-0', // 44x44px
        'icon-xl': 'size-12 p-0', // 48x48px
        'icon-2xl': 'size-14 p-0', // 56x56px
        auto: 'h-auto',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean;
    }
>(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    />
  );
});
Button.displayName = 'Button';

export { Button, buttonVariants };
